/**
 * Padrões de página do Design System (Fase B): cabeçalho, área de conteúdo, seções de
 * formulário, cartões de indicador, situações, estados (vazio, carregando, erro), barra de
 * pesquisa e filtros e tabela com rodapé. Só apresentação: nenhum deles busca dados nem decide
 * regra — quem usa entrega o conteúdo pronto.
 */
import { useRouter } from "@tanstack/react-router";
import { useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { MENU, isCurrentItem } from "@/app/menu";
import { Icon, type IconName } from "./icons";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

/**
 * Caminho da página atual tirado do menu lateral (módulo / página). Fora do roteador (ex.:
 * testes de uma tela isolada) não há caminho. Telas que precisam de outro caminho passam o seu.
 */
function useMenuTrail(title: string): string[] | undefined {
  const router: ReturnType<typeof useRouter> | null = useRouter({ warn: false });
  const pathname = router?.state.location.pathname;
  if (!pathname || pathname === "/") return undefined;
  for (const g of MENU) {
    const item = g.items.find((i) => isCurrentItem(i.to, pathname));
    if (!item || g.label === "Geral") continue;
    return item.label === title ? [g.label, item.label] : [g.label, item.label, title];
  }
  return undefined;
}

/** Cabeçalho da página: caminho, título, descrição curta e ação principal à direita. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: string;
  description?: string | undefined;
  actions?: ReactNode;
  /** ex.: ["Fiscal", "Documentos fiscais"] — o último é a página atual; sem ele, vem do menu */
  breadcrumb?: string[];
}) {
  const auto = useMenuTrail(title);
  const trail = breadcrumb ?? auto;
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 max-w-3xl">
        {trail && trail.length > 0 && (
          <nav aria-label="Caminho" className="mb-1.5 text-[13px] text-muted-foreground">
            {trail.map((b, i) => (
              <span key={`${b}-${i}`}>
                {i > 0 && <span aria-hidden> / </span>}
                <span className={i === trail.length - 1 ? "font-medium text-foreground" : ""}>
                  {b}
                </span>
              </span>
            ))}
          </nav>
        )}
        <h1 className="text-[26px] font-bold leading-tight tracking-tight sm:text-[28px]">
          {title}
        </h1>
        {description && (
          <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>
  );
}

/** Área de conteúdo com espaçamento padrão entre blocos. */
export function PageContainer({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6">{children}</div>;
}

/** Grupo de campos relacionados (com título e descrição opcionais) dentro de um formulário. */
export function FormSection({
  title,
  description,
  children,
  columns = 2,
}: {
  title?: string;
  description?: string;
  children: ReactNode;
  columns?: 1 | 2 | 3;
}) {
  return (
    <fieldset className="min-w-0 border-0 p-0">
      {title && <legend className="text-base font-semibold">{title}</legend>}
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      <div
        className={cx(
          "mt-4 grid gap-4",
          columns === 2 && "sm:grid-cols-2",
          columns === 3 && "sm:grid-cols-2 lg:grid-cols-3",
        )}
      >
        {children}
      </div>
    </fieldset>
  );
}

type SummaryTone = "neutral" | "ok" | "warn" | "error" | "info";
const DOT: Record<SummaryTone, string> = {
  neutral: "bg-muted-foreground",
  ok: "bg-success",
  warn: "bg-warning",
  error: "bg-destructive",
  info: "bg-info",
};

/** Cartão de indicador (ex.: total em aberto, vencidos). O valor chega já formatado. */
export function SummaryCard({
  label,
  value,
  hint,
  tone = "neutral",
  emphasize = false,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: SummaryTone;
  /** pinta o valor com a cor do tom (ex.: vencidos em vermelho) */
  emphasize?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border bg-card px-5 py-4 shadow-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-secondary-foreground">{label}</span>
        <span aria-hidden className={cx("size-2.5 rounded-full", DOT[tone])} />
      </div>
      <span
        className={cx(
          "text-[26px] font-bold leading-none tracking-tight tabular-nums",
          emphasize && tone === "error" && "text-destructive",
          emphasize && tone === "ok" && "text-success",
          emphasize && tone === "warn" && "text-warning-strong",
          emphasize && tone === "info" && "text-info",
        )}
      >
        {value}
      </span>
      {hint && <span className="text-[13px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

/** Estado vazio amigável: o que é, o que fazer e (quando couber) a ação. */
export function EmptyState({
  title,
  description,
  action,
  icon = "inbox",
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="flex flex-col items-center gap-2.5 rounded-xl border border-dashed border-input bg-background/60 px-6 py-10 text-center">
      <span className="text-caramel">
        <Icon name={icon} size={30} strokeWidth={1.6} />
      </span>
      <p className="text-[15px] font-semibold">{title}</p>
      {description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/** Carregando (anunciado ao leitor de tela). */
export function LoadingState({ label = "Carregando..." }: { label?: string }) {
  return (
    <p
      role="status"
      className="flex items-center justify-center gap-3 py-10 text-sm text-muted-foreground"
    >
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-caramel/30 border-t-caramel"
      />
      {label}
    </p>
  );
}

/** Erro ao carregar, com a mensagem e (opcional) o botão de tentar de novo. */
export function ErrorState({
  title = "Não foi possível carregar.",
  message,
  action,
}: {
  title?: string;
  message?: string;
  action?: ReactNode;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-2 rounded-xl border border-destructive/30 bg-destructive-soft px-6 py-8 text-center"
    >
      <span className="text-destructive">
        <Icon name="alert" size={26} />
      </span>
      <p className="text-[15px] font-semibold text-destructive">{title}</p>
      {message && <p className="max-w-md text-sm text-foreground">{message}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/**
 * Barra de pesquisa e filtros: campo de pesquisa destacado à esquerda, filtros agrupados ao
 * lado e, quando houver muitos, uma área de filtros avançados que abre e fecha.
 */
export function SearchAndFilters({
  search,
  onSearch,
  placeholder = "Pesquisar",
  children,
  advanced,
  advancedLabel = "Mais filtros",
}: {
  search: string;
  onSearch: (v: string) => void;
  placeholder?: string;
  children?: ReactNode;
  advanced?: ReactNode;
  advancedLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="relative flex min-w-[220px] flex-1 basis-72">
          <span className="sr-only">{placeholder}</span>
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Icon name="search" size={18} strokeWidth={2} />
          </span>
          <input
            type="search"
            value={search}
            placeholder={placeholder}
            onChange={(e) => onSearch(e.target.value)}
            className="h-11 w-full rounded-lg border border-input bg-card pl-11 pr-3.5 text-[15px] outline-none focus:border-caramel focus:ring-4 focus:ring-caramel/20"
          />
        </label>
        {children}
      </div>
      {advanced && (
        <details className="group rounded-lg border border-border/70 bg-background/60">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-primary">
            <Icon
              name="chevronRight"
              size={16}
              className="transition-transform group-open:rotate-90"
            />
            {advancedLabel}
          </summary>
          <div className="flex flex-wrap items-end gap-3 border-t border-border/70 px-3.5 py-3">
            {advanced}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * Tabela com barra superior (pesquisa/filtros), estado vazio e rodapé com a quantidade de
 * registros. O corpo continua sendo linhas `<tr>` montadas pela tela.
 */
export function DataTable({
  head,
  children,
  toolbar,
  count,
  empty,
  isEmpty = false,
  footer,
}: {
  head: { label: string; align?: "left" | "right" | "center"; srOnly?: boolean }[];
  children: ReactNode;
  toolbar?: ReactNode;
  /** ex.: "8 documentos" */
  count?: string;
  empty?: ReactNode;
  isEmpty?: boolean;
  footer?: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-card">
      {toolbar && <div className="border-b border-border/70 px-4 py-4">{toolbar}</div>}
      {isEmpty && empty ? (
        <div className="p-5">{empty}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm [&_tbody_td]:px-4 [&_tbody_td]:py-3 [&_tbody_tr:hover]:bg-background">
            <thead className="bg-table-head text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                {head.map((h, i) => (
                  <th
                    key={`${h.label}-${i}`}
                    scope="col"
                    className={cx(
                      "px-4 py-3 font-semibold",
                      h.align === "right" && "text-right",
                      h.align === "center" && "text-center",
                    )}
                  >
                    {h.srOnly ? <span className="sr-only">{h.label}</span> : h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">{children}</tbody>
          </table>
        </div>
      )}
      {(count || footer) && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border/70 px-4 py-3 text-sm text-muted-foreground">
          {count && <span>{count}</span>}
          <span className="flex-1" />
          {footer}
        </div>
      )}
    </section>
  );
}

/**
 * Andamento de um documento (só leitura): ex.: Rascunho → Aprovado → Confirmado. Mostra onde o
 * documento está; as ações continuam nos botões da tela. `current` = -1 quando o documento saiu
 * do fluxo (cancelado/estornado), e `note` explica.
 */
export function ProgressSteps({
  label,
  steps,
  current,
  note,
}: {
  label: string;
  steps: string[];
  /** índice da etapa atual; etapas antes dela estão concluídas */
  current: number;
  note?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <ol aria-label={label} className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {steps.map((s, i) => {
          const done = current >= 0 && i < current;
          const isCurrent = i === current;
          return (
            <li
              key={s}
              aria-current={isCurrent ? "step" : undefined}
              className="flex items-center gap-1.5 text-sm"
            >
              <span
                aria-hidden
                className={cx(
                  "flex size-6 items-center justify-center rounded-full text-xs font-semibold",
                  isCurrent && "bg-accent text-accent-foreground",
                  done && "bg-success-soft text-success",
                  !isCurrent && !done && "border border-input text-muted-foreground",
                )}
              >
                {done ? <Icon name="check" size={12} strokeWidth={3} /> : i + 1}
              </span>
              <span
                className={cx(
                  isCurrent ? "font-semibold" : done ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {s}
              </span>
              {done && <span className="sr-only">(concluída)</span>}
              {i < steps.length - 1 && (
                <span aria-hidden className="mx-1 h-0.5 w-5 rounded bg-input" />
              )}
            </li>
          );
        })}
      </ol>
      {note && <span className="text-sm text-muted-foreground">{note}</span>}
    </div>
  );
}

/** Informações-chave em grade (rótulo em cima, valor embaixo). */
export function KeyFacts({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-x-6 gap-y-3 rounded-lg bg-background/70 px-4 py-3">
      {items.map((it) => (
        <div key={it.label} className="min-w-0">
          <dt className="text-xs font-medium text-muted-foreground">{it.label}</dt>
          <dd className="mt-0.5 text-sm font-medium break-words">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Abas acessíveis (setas ←/→, Home/End): o conteúdo de cada aba é desenhado só quando ela está
 * aberta. Use para agrupar detalhes de um mesmo registro (ex.: nota fiscal).
 */
export function Tabs({
  label,
  tabs,
  initial,
}: {
  label: string;
  tabs: { key: string; label: string; content: ReactNode; badge?: string | number }[];
  initial?: string;
}) {
  const [current, setCurrent] = useState(initial ?? tabs[0]?.key ?? "");
  const baseId = useId();
  const active = tabs.find((t) => t.key === current) ?? tabs[0];
  const focusTab = (key: string) => {
    setCurrent(key);
    document.getElementById(`${baseId}-tab-${key}`)?.focus();
  };
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = tabs.findIndex((t) => t.key === active?.key);
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    const t = tabs[next];
    if (t) focusTab(t.key);
  }
  return (
    <div>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        // rolagem só na horizontal (telas estreitas); a linha de base é uma sombra interna,
        // então a borda da aba ativa não ultrapassa a área e não cria rolagem vertical
        className="flex gap-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--color-border)] [scrollbar-width:thin]"
      >
        {tabs.map((t) => {
          const selected = t.key === active?.key;
          return (
            <button
              key={t.key}
              id={`${baseId}-tab-${t.key}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${t.key}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setCurrent(t.key)}
              className={cx(
                "flex min-h-11 shrink-0 items-center gap-2 border-b-[3px] px-4 text-sm whitespace-nowrap focus-visible:-outline-offset-2",
                selected
                  ? "border-caramel font-semibold text-foreground"
                  : "border-transparent font-medium text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
              {t.badge !== undefined && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {active && (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${active.key}`}
          aria-labelledby={`${baseId}-tab-${active.key}`}
          tabIndex={0}
          className="pt-5 outline-none"
        >
          {active.content}
        </div>
      )}
    </div>
  );
}

/** Seção numerada de um processo longo (uma etapa à vista, na ordem em que se preenche). */
export function NumberedSection({
  n,
  title,
  description,
  actions,
  children,
}: {
  n: number;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="rounded-xl border bg-card p-5 shadow-card">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex size-7 shrink-0 items-center justify-center rounded-full bg-caramel-soft text-[13px] font-semibold text-accent"
          >
            {n}
          </span>
          <div>
            <h3 id={id} className="text-base font-semibold">
              {title}
            </h3>
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}
